import type { PhaseView } from '../lib/checklist-view';

/** Bring a heading of the list into view (it clears the sticky header). */
function jumpTo(id: string): void {
  const el = document.getElementById(id);
  el?.scrollIntoView?.({ block: 'start' });
  el?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
}

/**
 * The category outline (redesign slice 3): every shown phase and its categories,
 * each with how many items are still missing — so a 400-item checklist can be
 * crossed in one click. A rail beside the list on a wide screen; a "Jump to…"
 * select above it on a phone (see {@link ChecklistJumpTo}).
 */
export function ChecklistOutline({ views }: { views: PhaseView[] }) {
  return (
    <nav
      aria-label="Checklist outline"
      className="hidden h-fit max-h-[calc(100vh-6rem)] overflow-y-auto pr-1 text-sm lg:sticky lg:top-20 lg:block"
    >
      <ul className="space-y-3">
        {views.map((view) => (
          <li key={view.key}>
            <button
              type="button"
              className="w-full rounded px-1.5 py-0.5 text-left font-semibold text-ink hover:bg-surface-2"
              onClick={() => jumpTo(`phase-${view.key}-h`)}
            >
              {view.phase.name ?? 'Items not in a phase'}
            </button>
            {view.groups.length > 0 ? (
              <ul className="mt-0.5 space-y-px">
                {view.groups.map((g) => {
                  // The leaf first (it is what differs), its parent quietly under it.
                  const parts = g.leaf.split(' › ');
                  const leaf = parts[parts.length - 1];
                  const parent = parts.length > 1 ? parts[0] : null;
                  return (
                    <li key={g.id}>
                      <button
                        type="button"
                        title={g.heading}
                        className="flex w-full items-baseline justify-between gap-2 rounded px-1.5 py-0.5 text-left text-ink-2 hover:bg-surface-2 hover:text-ink"
                        onClick={() => jumpTo(g.id)}
                      >
                        <span className="min-w-0">
                          <span className="block truncate">{leaf}</span>
                          {parent ? (
                            <span className="block truncate text-[11px] text-ink-3">{parent}</span>
                          ) : null}
                        </span>
                        {g.missing > 0 ? (
                          <span className="shrink-0 text-xs tabular-nums text-brass">{g.missing}</span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** The outline as one select, for a phone (below the wide-screen breakpoint). */
export function ChecklistJumpTo({ views }: { views: PhaseView[] }) {
  if (views.length === 0) return null;
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-ink-2 lg:hidden">
      Jump to
      <select
        value=""
        onChange={(e) => {
          if (e.target.value) jumpTo(e.target.value);
        }}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-ink max-md:min-h-tap"
      >
        <option value="">Choose a phase or category…</option>
        {views.map((view) => (
          <optgroup key={view.key} label={view.phase.name ?? 'Items not in a phase'}>
            <option value={`phase-${view.key}-h`}>{view.phase.name ?? 'Items not in a phase'}</option>
            {view.groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.heading}
                {g.missing > 0 ? ` (${g.missing} missing)` : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}
